import { sdk } from '@/services/sdk';
import { rows } from '@/shared/data';

const { eq } = window.ApperSDK.CoreSDK;

/** Picklist values stored on status_services_c.status_c, best to worst. */
export const STATUS_VALUES = ['Up', 'Maintenance', 'Degraded', 'Errors', 'Down'];

/** What each stored value looks like on the public page and in the console. */
export const STATUS_META = {
  Up: {
    label: 'Operational',
    icon: 'CircleCheck',
    rank: 0,
    badge: 'border-success-border bg-success-muted text-success',
    dot: 'bg-success',
    text: 'text-success',
    headline: 'All systems operational',
    blurb: 'Everything is streaming normally.',
  },
  Maintenance: {
    label: 'Maintenance',
    icon: 'Wrench',
    rank: 1,
    badge: 'border-highlight/40 bg-highlight/15 text-highlight',
    dot: 'bg-highlight',
    text: 'text-highlight',
    headline: 'Scheduled maintenance',
    blurb: "We're working on part of the service. It will be back shortly.",
  },
  Degraded: {
    label: 'Having issues',
    icon: 'TriangleAlert',
    rank: 2,
    badge: 'border-warning-border bg-warning-muted text-warning',
    dot: 'bg-warning',
    text: 'text-warning',
    headline: 'Some services are having issues',
    blurb: 'You may notice buffering or slow loading. We are on it.',
  },
  Errors: {
    label: 'Connection errors',
    icon: 'WifiOff',
    rank: 3,
    badge: 'border-destructive/40 bg-destructive/10 text-destructive',
    dot: 'bg-destructive',
    text: 'text-destructive',
    headline: 'Connection errors',
    blurb: "Some connections are failing right now. We're working on a fix.",
  },
  Down: {
    label: 'Down',
    icon: 'CircleX',
    rank: 4,
    badge: 'border-destructive/40 bg-destructive/10 text-destructive',
    dot: 'bg-destructive',
    text: 'text-destructive',
    headline: 'Service outage',
    blurb: "Part of Ghost Cable is down. We're working to bring it back.",
  },
};

export const STATUS_LABELS = Object.fromEntries(STATUS_VALUES.map((v) => [v, STATUS_META[v].label]));

export const metaOf = (value) => STATUS_META[value] ?? STATUS_META.Up;

/** Public fields only. Links live in status_links_c, which only Admin and Staff can read. */
export const STATUS_FIELDS = ['Name', 'status_c', 'message_c', 'sort_c', 'active_c', 'ModifiedOn'];

export const LINK_FIELDS = ['Name', 'service_c', 'urls_c'];

const bySort = (a, b) =>
  (Number(a.sort_c) || 0) - (Number(b.sort_c) || 0) || String(a.Name ?? '').localeCompare(String(b.Name ?? ''));

/** Services shown on the public status page. */
export function loadPublicStatus() {
  return sdk
    .table('status_services_c')
    .select(STATUS_FIELDS)
    .where(eq('active_c', true))
    .orderBy('sort_c')
    .limit(100)
    .fetch()
    .then(rows)
    .then((list) => [...list].sort(bySort));
}

/** Every service plus its private links, for the console. */
export async function loadAdminStatus() {
  const [services, links] = await Promise.all([
    sdk.table('status_services_c').select(STATUS_FIELDS).orderBy('sort_c').limit(200).fetch().then(rows),
    sdk.table('status_links_c').select(LINK_FIELDS).limit(500).fetch().then(rows),
  ]);
  const linkBy = new Map();
  links.forEach((l) => {
    const id = l.service_c?.Id ?? l.service_c;
    if (id != null) linkBy.set(String(id), l);
  });
  return [...services].sort(bySort).map((s) => ({ ...s, link: linkBy.get(String(s.Id)) ?? null }));
}

/** The worst status across services wins. Null when nothing is listed. */
export function overallOf(list) {
  if (!list?.length) return null;
  return list.reduce((worst, s) => (metaOf(s.status_c).rank > metaOf(worst).rank ? s.status_c : worst), 'Up');
}

export const latestUpdate = (list) =>
  (list ?? []).reduce((max, s) => (s.ModifiedOn && (!max || s.ModifiedOn > max) ? s.ModifiedOn : max), null);

/** Links textarea -> trimmed list, one per line. */
export const splitLinks = (text) =>
  String(text ?? '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);

export const STATUS_PATH = '/status';
